/**
 * Mock Supplier Data
 * Contains sample suppliers and ingredients for the halal supply chain
 */

import type { Supplier, Ingredient } from '../types/supplier';
import {
  SupplierType,
  SupplierStatus,
  HalalCertificationStatus,
} from '../types/supplier';

/**
 * Mock Suppliers
 */
export const mockSuppliers: Supplier[] = [
  // Certified Ingredient Supplier
  {
    id: 'sup-001',
    companyId: 'org-001',
    name: 'Golden Rice Trading Sdn Bhd',
    type: SupplierType.INGREDIENTS,
    status: SupplierStatus.ACTIVE,

    contactPerson: {
      name: 'Lee Mei Ling',
      position: 'Sales Manager',
      email: 'meiling@goldenrice.com.my',
      phone: '+60321234567',
      mobile: '+60123456781',
    },

    address: {
      street: 'No. 45, Jalan Industri 3',
      city: 'Klang',
      state: 'Selangor',
      postalCode: '41000',
      country: 'Malaysia',
    },

    halalCertified: true,
    certificationStatus: HalalCertificationStatus.CERTIFIED,
    certificationNumber: 'JAKIM-2024-1234',
    certificationBody: 'JAKIM',
    certificationIssueDate: new Date('2024-01-15'),
    certificationExpiryDate: new Date('2026-01-14'),
    certificateUrl: '/mock/certs/golden-rice-halal-cert.pdf',

    productsProvided: ['Jasmine Rice', 'Basmati Rice', 'Brown Rice', 'Rice Flour'],
    materialCategories: ['Grains', 'Flour'],

    riskLevel: 'LOW',
    lastAuditDate: new Date('2024-01-10'),
    nextAuditDate: new Date('2026-01-10'),

    createdAt: new Date('2025-11-01'),
    updatedAt: new Date('2025-12-15'),
    createdBy: 'user-002',
  },

  // Certified Spice Supplier
  {
    id: 'sup-002',
    companyId: 'org-001',
    name: 'Rempah Wangi Industries',
    type: SupplierType.INGREDIENTS,
    status: SupplierStatus.ACTIVE,

    contactPerson: {
      name: 'Ahmad Zaki',
      position: 'Business Development Manager',
      email: 'zaki@rempahwangi.com.my',
      phone: '+60356781234',
      mobile: '+60198765433',
    },

    address: {
      street: 'Lot 88, Kawasan Perindustrian',
      city: 'Nilai',
      state: 'Negeri Sembilan',
      postalCode: '71800',
      country: 'Malaysia',
    },

    halalCertified: true,
    certificationStatus: HalalCertificationStatus.CERTIFIED,
    certificationNumber: 'JAKIM-2023-5678',
    certificationBody: 'JAKIM',
    certificationIssueDate: new Date('2023-06-01'),
    certificationExpiryDate: new Date('2025-05-31'),
    certificateUrl: '/mock/certs/rempah-wangi-halal-cert.pdf',

    productsProvided: [
      'Chili Powder',
      'Turmeric Powder',
      'Coriander Powder',
      'Cumin Seeds',
      'Mixed Spice Blends',
    ],
    materialCategories: ['Spices', 'Seasonings'],

    riskLevel: 'LOW',
    lastAuditDate: new Date('2023-05-15'),
    nextAuditDate: new Date('2025-05-15'),

    createdAt: new Date('2025-11-02'),
    updatedAt: new Date('2025-12-10'),
    createdBy: 'user-002',
  },

  // Packaging Supplier (Certified)
  {
    id: 'sup-003',
    companyId: 'org-001',
    name: 'Eco Pack Solutions',
    type: SupplierType.PACKAGING,
    status: SupplierStatus.ACTIVE,

    contactPerson: {
      name: 'Rajesh Kumar',
      position: 'Account Manager',
      email: 'rajesh@ecopack.com.my',
      phone: '+60378901234',
      mobile: '+60122345678',
    },

    address: {
      street: '123, Jalan Packaging',
      city: 'Petaling Jaya',
      state: 'Selangor',
      postalCode: '47600',
      country: 'Malaysia',
    },

    halalCertified: true,
    certificationStatus: HalalCertificationStatus.CERTIFIED,
    certificationNumber: 'JAKIM-2024-9012',
    certificationBody: 'JAKIM',
    certificationIssueDate: new Date('2024-03-01'),
    certificationExpiryDate: new Date('2026-02-28'),
    certificateUrl: '/mock/certs/eco-pack-halal-cert.pdf',

    productsProvided: [
      'Food-Grade Plastic Containers',
      'Vacuum Seal Bags',
      'Cardboard Boxes',
      'Labels',
    ],
    materialCategories: ['Packaging Materials', 'Food Contact Materials'],

    riskLevel: 'LOW',
    lastAuditDate: new Date('2024-02-15'),
    nextAuditDate: new Date('2026-02-15'),

    createdAt: new Date('2025-11-05'),
    updatedAt: new Date('2025-12-20'),
    createdBy: 'user-002',
  },

  // Service Provider (Cleaning - Certified)
  {
    id: 'sup-004',
    companyId: 'org-001',
    name: 'Halal Clean Services',
    type: SupplierType.SERVICES,
    status: SupplierStatus.ACTIVE,

    contactPerson: {
      name: 'Fatimah Zahra',
      position: 'Operations Manager',
      email: 'fatimah@halalclean.com.my',
      phone: '+60389012345',
      mobile: '+60176543210',
    },

    address: {
      street: 'Unit 12, Plaza Perniagaan',
      city: 'Shah Alam',
      state: 'Selangor',
      postalCode: '40150',
      country: 'Malaysia',
    },

    halalCertified: true,
    certificationStatus: HalalCertificationStatus.CERTIFIED,
    certificationNumber: 'JAKIM-2024-3456',
    certificationBody: 'JAKIM',
    certificationIssueDate: new Date('2024-02-01'),
    certificationExpiryDate: new Date('2026-01-31'),
    certificateUrl: '/mock/certs/halal-clean-halal-cert.pdf',

    productsProvided: [
      'Factory Cleaning Services',
      'Equipment Sanitization',
      'Pest Control',
    ],
    materialCategories: ['Cleaning Services', 'Maintenance'],

    riskLevel: 'MEDIUM',
    lastAuditDate: new Date('2024-01-20'),
    nextAuditDate: new Date('2026-01-20'),

    createdAt: new Date('2025-11-08'),
    updatedAt: new Date('2025-12-18'),
    createdBy: 'user-002',
  },

  // Ingredient Supplier (Cert Expiring Soon)
  {
    id: 'sup-005',
    companyId: 'org-001',
    name: 'Coconut King Suppliers',
    type: SupplierType.INGREDIENTS,
    status: SupplierStatus.ACTIVE,

    contactPerson: {
      name: 'Muthu Selvam',
      position: 'Director',
      email: 'muthu@coconutking.com.my',
      phone: '+60345678901',
      mobile: '+60134567890',
    },

    address: {
      street: 'Lot 67, Kawasan Perindustrian Kelapa',
      city: 'Kuala Lumpur',
      state: 'Wilayah Persekutuan',
      postalCode: '50450',
      country: 'Malaysia',
    },

    halalCertified: true,
    certificationStatus: HalalCertificationStatus.CERTIFIED,
    certificationNumber: 'JAKIM-2023-7890',
    certificationBody: 'JAKIM',
    certificationIssueDate: new Date('2023-02-01'),
    certificationExpiryDate: new Date('2026-01-15'), // Expiring in ~15 days
    certificateUrl: '/mock/certs/coconut-king-halal-cert.pdf',

    productsProvided: ['Coconut Milk', 'Coconut Cream', 'Desiccated Coconut'],
    materialCategories: ['Dairy Alternatives', 'Ingredients'],

    riskLevel: 'MEDIUM',
    lastAuditDate: new Date('2023-01-15'),
    nextAuditDate: new Date('2026-01-15'),

    createdAt: new Date('2025-11-10'),
    updatedAt: new Date('2025-12-01'),
    createdBy: 'user-002',
  },

  // Equipment Supplier (Not Certified - OK for equipment)
  {
    id: 'sup-006',
    companyId: 'org-001',
    name: 'Industrial Equipment Supply Co',
    type: SupplierType.EQUIPMENT,
    status: SupplierStatus.ACTIVE,

    contactPerson: {
      name: 'Wong Wei Lun',
      position: 'Sales Executive',
      email: 'wong@industrialequip.com.my',
      phone: '+60312345678',
      mobile: '+60187654321',
    },

    address: {
      street: '456, Jalan Peralatan',
      city: 'Subang Jaya',
      state: 'Selangor',
      postalCode: '47500',
      country: 'Malaysia',
    },

    halalCertified: false,
    certificationStatus: HalalCertificationStatus.NOT_CERTIFIED,

    productsProvided: [
      'Industrial Mixers',
      'Conveyor Belts',
      'Packaging Machines',
      'Storage Tanks',
    ],
    materialCategories: ['Equipment', 'Machinery'],

    riskLevel: 'LOW',

    createdAt: new Date('2025-11-15'),
    updatedAt: new Date('2025-12-05'),
    createdBy: 'user-002',
  },
];

/**
 * Mock Ingredients
 */
export const mockIngredients: Ingredient[] = [
  {
    id: 'ing-001',
    supplierId: 'sup-001',
    companyId: 'org-001',
    name: 'Premium Jasmine Rice',
    description: 'Thai fragrant rice for premium nasi lemak',
    category: 'Grains',
    type: 'Rice',
    halalStatus: 'HALAL',
    certificateNumber: 'JAKIM-2024-1234',
    certificateUrl: '/mock/certs/golden-rice-halal-cert.pdf',
    certificationExpiryDate: new Date('2026-01-14'),
    usedInProducts: ['Frozen Nasi Lemak', 'Ready-to-Eat Meals'],
    monthlyUsageQuantity: 500,
    unit: 'kg',
    createdAt: new Date('2025-11-01'),
    updatedAt: new Date('2025-12-15'),
  },
  {
    id: 'ing-002',
    supplierId: 'sup-002',
    companyId: 'org-001',
    name: 'Red Chili Powder',
    description: 'Ground dried red chilies for sambal',
    category: 'Spices',
    type: 'Chili',
    halalStatus: 'HALAL',
    certificateNumber: 'JAKIM-2023-5678',
    certificateUrl: '/mock/certs/rempah-wangi-halal-cert.pdf',
    certificationExpiryDate: new Date('2025-05-31'),
    usedInProducts: ['Sambal Paste', 'Spice Blends'],
    monthlyUsageQuantity: 50,
    unit: 'kg',
    createdAt: new Date('2025-11-02'),
    updatedAt: new Date('2025-12-10'),
  },
  {
    id: 'ing-003',
    supplierId: 'sup-005',
    companyId: 'org-001',
    name: 'Coconut Milk',
    description: 'Creamy coconut milk for cooking',
    category: 'Dairy Alternatives',
    type: 'Coconut Product',
    halalStatus: 'HALAL',
    certificateNumber: 'JAKIM-2023-7890',
    certificateUrl: '/mock/certs/coconut-king-halal-cert.pdf',
    certificationExpiryDate: new Date('2026-01-15'),
    usedInProducts: ['Frozen Nasi Lemak', 'Ready-to-Eat Meals', 'Sambal Paste'],
    monthlyUsageQuantity: 200,
    unit: 'L',
    createdAt: new Date('2025-11-10'),
    updatedAt: new Date('2025-12-01'),
  },
];

/**
 * Helper to get supplier by ID
 */
export function getSupplierById(id: string): Supplier | undefined {
  return mockSuppliers.find((sup) => sup.id === id);
}

/**
 * Helper to get suppliers by company
 */
export function getSuppliersByCompanyId(companyId: string): Supplier[] {
  return mockSuppliers.filter((sup) => sup.companyId === companyId);
}

/**
 * Helper to get suppliers by type
 */
export function getSuppliersByType(type: SupplierType): Supplier[] {
  return mockSuppliers.filter((sup) => sup.type === type);
}

/**
 * Helper to get certified suppliers
 */
export function getCertifiedSuppliers(): Supplier[] {
  return mockSuppliers.filter((sup) => sup.halalCertified);
}

/**
 * Helper to get suppliers with expiring certificates
 */
export function getSuppliersWithExpiringCerts(daysThreshold: number = 30): Supplier[] {
  const today = new Date();
  return mockSuppliers.filter((sup) => {
    if (!sup.certificationExpiryDate) return false;
    const daysUntilExpiry = Math.ceil(
      (sup.certificationExpiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry > 0 && daysUntilExpiry <= daysThreshold;
  });
}

/**
 * Helper to get ingredient by ID
 */
export function getIngredientById(id: string): Ingredient | undefined {
  return mockIngredients.find((ing) => ing.id === id);
}

/**
 * Helper to get ingredients by supplier
 */
export function getIngredientsBySupplierId(supplierId: string): Ingredient[] {
  return mockIngredients.filter((ing) => ing.supplierId === supplierId);
}

/**
 * Helper to get ingredients by halal status
 */
export function getIngredientsByHalalStatus(
  status: 'HALAL' | 'NON_HALAL' | 'MUSHBOOH' | 'PENDING_VERIFICATION'
): Ingredient[] {
  return mockIngredients.filter((ing) => ing.halalStatus === status);
}
